/* eslint-disable global-require */
import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { NextPage, GetStaticProps, GetStaticPaths } from 'next';

import {
  courtAttendanceNotice,
  licenceSuspension,
  penaltyNotice,
} from '@/pathways';
import { Page } from '@/components/layout';
import { Calculating, Pathway } from '@/components/pages';
import { assertNever } from '@/utils/types';
import generateMetaTags from '@/utils/meta';
import type { MetaTagProps } from '@/utils/meta';

type PathwaySlug =
  | 'court-attendance-notice'
  | 'licence-suspension'
  | 'penalty-notice';

const getMetaTagProps = (pathway: PathwaySlug): MetaTagProps => {
  switch (pathway) {
    case 'court-attendance-notice':
      return {
        title: 'Have you received a court attendance notice?',
        description:
          'If you have received a notice to attend court, we can let you know what your options are and how to prepare.',
        image: require('@/images/person-at-laptop.jpg'),
      };
    case 'licence-suspension':
      return {
        title: 'Has your licence been suspended?',
        description:
          "If your licence has been suspended, or you've received notice that it will be, we can let you know what you can do about it.",
        image: require('@/images/police-traffic-stop.jpg'),
      };
    case 'penalty-notice':
      return {
        title: 'Have you received a penalty notice?',
        description:
          "No matter the traffic problem, it's important not to ignore it. We can help you understand your options for dealing with a penalty notice.",
        image: require('@/images/people-taking-notes.jpg'),
      };
    default:
      return assertNever(pathway);
  }
};

type Props = {
  pathway: PathwaySlug;
};

const PathwayPage: NextPage<Props> = ({ pathway }) => {
  // On client-side use the param from the browser URL
  // This allows the browser back button to correctly navigate between pathways
  const router = useRouter();
  const routerPathway = router.query.slug?.[0] as PathwaySlug;

  // Display loading indicator on pre-rendered page
  // Use the pathway param to generate the correct meta tags for the static HTML
  const [firstRender, setFirstRender] = useState(true);
  useEffect(() => {
    setFirstRender(false);
  }, []);
  if (firstRender) {
    return (
      <Page>
        <Head>{generateMetaTags(getMetaTagProps(pathway))}</Head>
        <Calculating text="" />
      </Page>
    );
  }

  switch (routerPathway) {
    case 'court-attendance-notice':
      return (
        <>
          <Head>
            {generateMetaTags(getMetaTagProps('court-attendance-notice'))}
          </Head>
          <Pathway
            key="court-attendance-notice"
            machine={courtAttendanceNotice.machine}
            questions={courtAttendanceNotice.questions}
          />
        </>
      );
    case 'licence-suspension':
      return (
        <>
          <Head>{generateMetaTags(getMetaTagProps('licence-suspension'))}</Head>
          <Pathway
            key="licence-suspension"
            machine={licenceSuspension.machine}
            questions={licenceSuspension.questions}
          />
        </>
      );
    case 'penalty-notice':
      return (
        <>
          <Head>{generateMetaTags(getMetaTagProps('penalty-notice'))}</Head>
          <Pathway
            key="penalty-notice"
            machine={penaltyNotice.machine}
            questions={penaltyNotice.questions}
          />
        </>
      );
    default:
      return null;
  }
};

export const getStaticProps: GetStaticProps = async (context) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const pathway = context.params!.slug![0] as PathwaySlug;
  return {
    props: { pathway },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      {
        params: {
          slug: ['court-attendance-notice'],
        },
      },
      {
        params: {
          slug: ['licence-suspension'],
        },
      },
      {
        params: {
          slug: ['penalty-notice'],
        },
      },
    ],
    fallback: false,
  };
};

export default PathwayPage;
